/**
 * Co-parentage — Réglages (port `vCpSettings` proto l. 2205-2300).
 *
 * Phase 1, incrément 18. Vue minimale qui couvre la **configuration
 * de garde** — base critique pour toutes les vues co-parentage
 * (CpHome / CpActivities / CpFrais / CpBag).
 *
 * Sections portées :
 *   - Custody : parentA, parentB, startDate, weekParity (toggle Chez A
 *     / Chez B en semaine 1).
 *   - Lien rapide vers `/cp/school` (route hors nav, cf. CLAUDE.md §2.1
 *     limite 5 onglets co-parentage).
 *
 * **Skipped** Phase 1 (à porter Phase 4 avec auth/api) :
 *   - Gestion enfants concernés (photos, lien foyer principal)
 *   - Invitation second parent par email
 *   - Logout / Mon compte
 *   - RGPD (export, suppression compte)
 */
import { html, type TemplateResult } from 'lit-html';
import styles from './SettingsView.module.css';
import { t } from '../../i18n/index.js';
import { getCurrentHome, updateCustodyConfig } from '../../stores/homes.js';
import { navigate } from '../../lib/router.js';
import { bumpUi } from '../../stores/ui.js';
import type { CoparentingHomeData } from '@nestor/shared-types';
import { editorialHero } from '../../ui/Hero.js';

interface FormState {
  parentA: string;
  parentB: string;
  startDate: string;
  weekParity: 0 | 1;
  /** Vrai pendant ~1.5 s après un save réussi pour afficher « ✓ Enregistré ». */
  savedFlash: boolean;
}

const formState: FormState = {
  parentA: '',
  parentB: '',
  startDate: '',
  weekParity: 0,
  savedFlash: false,
};

let initializedForHomeId: string | null = null;

function ensureFormInit(home: { id: string; data: CoparentingHomeData }): void {
  // Charge les valeurs depuis le store la 1re fois qu'on rend la vue
  // pour ce foyer (et après bascule de foyer).
  if (initializedForHomeId === home.id) return;
  const { custody } = home.data;
  formState.parentA = custody.parentA;
  formState.parentB = custody.parentB;
  formState.startDate = custody.startDate;
  formState.weekParity = custody.weekParity;
  formState.savedFlash = false;
  initializedForHomeId = home.id;
}

function flashSavedThenClear(): void {
  formState.savedFlash = true;
  bumpUi();
  setTimeout(() => {
    formState.savedFlash = false;
    bumpUi();
  }, 1500);
}

function custodyCardTemplate(home: { id: string; data: CoparentingHomeData }): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    const ok = updateCustodyConfig(home.id, {
      parentA: formState.parentA,
      parentB: formState.parentB,
      startDate: formState.startDate,
      weekParity: formState.weekParity,
    });
    if (ok) flashSavedThenClear();
  };
  return html`<form class=${styles.card} @submit=${onSubmit}>
    <div class=${styles.cardTitle}>${t('cp_settings_custody_title')}</div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_settings_label_parentA')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cp-settings-parentA"
        .value=${formState.parentA}
        @input=${(e: Event): void => {
          formState.parentA = (e.target as HTMLInputElement).value;
        }}
        required
      />
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_settings_label_parentB')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cp-settings-parentB"
        .value=${formState.parentB}
        @input=${(e: Event): void => {
          formState.parentB = (e.target as HTMLInputElement).value;
        }}
        required
      />
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_settings_label_startdate')}</label>
      <input
        class=${styles.formInput}
        type="date"
        data-testid="cp-settings-startdate"
        .value=${formState.startDate}
        @input=${(e: Event): void => {
          formState.startDate = (e.target as HTMLInputElement).value;
        }}
        required
      />
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_settings_label_parity')}</label>
      <select
        class=${styles.formSelect}
        data-testid="cp-settings-parity"
        .value=${formState.weekParity.toString()}
        @change=${(e: Event): void => {
          const v = (e.target as HTMLSelectElement).value;
          formState.weekParity = v === '1' ? 1 : 0;
        }}
      >
        <option value="0">${t('cp_settings_parity_a')}</option>
        <option value="1">${t('cp_settings_parity_b')}</option>
      </select>
    </div>
    <button type="submit" class=${styles.saveBtn} data-testid="cp-settings-save">
      ${t('cp_settings_btn_save')}
    </button>
    ${formState.savedFlash
      ? html`<div class=${styles.savedFlash} data-testid="cp-settings-saved">
          ${t('cp_settings_saved')}
        </div>`
      : html``}
  </form>`;
}

function schoolLinkTemplate(): TemplateResult {
  const onClick = (): void => {
    navigate('/cp/school');
  };
  return html`<button
    type="button"
    class=${styles.linkBtn}
    data-testid="cp-settings-view-school"
    @click=${onClick}
  >
    ${t('cp_settings_view_school')}
  </button>`;
}

export function renderCpSettingsView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'coparenting') {
    initializedForHomeId = null;
    return html`<section class=${styles.view} data-testid="view-cp-settings"></section>`;
  }
  ensureFormInit({ id: home.id, data: home.data as CoparentingHomeData });
  return html`
    <section class=${styles.view} data-testid="view-cp-settings">
      ${editorialHero({
        badge: { mode: 'coparenting', label: home.name },
        dateLine: t('cp_settings_dateline'),
        title: t('settings_hero_title_cp'),
        accent: t('settings_hero_accent_cp'),
      })}
      ${custodyCardTemplate({ id: home.id, data: home.data as CoparentingHomeData })}
      ${schoolLinkTemplate()}
    </section>
  `;
}

/** @internal pour tests — reset l'état module-local. */
export function _resetCpSettingsForTests(): void {
  formState.parentA = '';
  formState.parentB = '';
  formState.startDate = '';
  formState.weekParity = 0;
  formState.savedFlash = false;
  initializedForHomeId = null;
}
